package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.FontAccess
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess

/**
 * Dropdown 渲染器（接入主题，Task 2a 支持逐项覆盖）。
 * - 头部 bg：DSL > `surfaceContainerHigh`；SV 圆角 / 渐变存在时走 styled batch；
 *   边框 focusBorderColor（覆盖）> focused 主题 primary > outline
 * - 头部 / option 文本：textColor（覆盖 > onSurface）；箭头：arrowColor（覆盖 > onSurface）
 * - option 行 bg：panelColor（覆盖 > surface）；选中：selectedOptionColor（覆盖 > primaryContainer）；
 *   选中文本：selectedOptionTextColor（覆盖 > onPrimaryContainer）；行高 optionHeight（覆盖 > 常量）
 */
@Suppress("MagicNumber")
class DropdownRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val d = element as Dropdown
        val t = ThemeAccess.current()
        val pad = t.textPaddingX

        val focusBorder = d.focusBorderColor() ?: t.primary
        val border = if (d.focused()) focusBorder else t.outline
        val textColor = d.textColor() ?: t.onSurface
        val arrowColor = d.arrowColor() ?: t.onSurface
        // 头部背景：覆盖优先，否则主题 surfaceContainerHigh。
        val headerColor = d.headerColor() ?: t.surfaceContainerHigh

        // 背景 + 边框：SV 圆角 / 渐变存在时走 styled 路径，否则退化为 fillRect 背景 + 4 条边框。
        ControlSurfaceRenderer.drawSurface(bridge, d, box, headerColor, border, t.borderWidth)

        val font = FontAccess.resolve(d.font())
        val fontName = font.effectiveName()
        val shadow = font.effectiveShadow()
        val bold = font.effectiveBold()
        val italic = font.effectiveItalic()
        val scale = d.size()

        // 头部：选中项 label（回退到 selectedValue）；文字与箭头垂直居中（8px 字高），
        // 颜色用覆盖属性 textColor/arrowColor，字体走 FontAccess（合并 HEAD 字体系统 + 控件覆盖）。
        val headerTextY = box.y() + (box.height() - 8f) / 2f
        val headerText = d.selectedOptionLabel()
        if (!headerText.isNullOrEmpty()) {
            bridge.drawText(headerText, box.x() + pad, headerTextY, textColor, scale, fontName, shadow, bold, italic)
        }
        val arrow = if (d.expanded()) "^" else "v"
        val arrowW = bridge.textWidth(arrow, scale, fontName, bold)
        bridge.drawText(
            arrow,
            box.x() + box.width() - arrowW - pad,
            headerTextY,
            arrowColor,
            scale,
            fontName,
            shadow,
            bold,
            italic,
        )
    }

    fun renderOverlay(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val d = element as Dropdown
        if (!d.expanded()) {
            return
        }
        val t = ThemeAccess.current()
        val focusBorder = d.focusBorderColor() ?: t.primary
        val border = if (d.focused()) focusBorder else t.outline
        renderPanel(d, box, bridge, border, t)
    }

    private fun renderPanel(d: Dropdown, box: Box, bridge: UiRenderBridge, border: Int, t: ControlTheme) {
        val bw = t.borderWidth
        // option 文本左右内边距：覆盖优先，否则主题 textPaddingX。
        val pad = d.optionPaddingX() ?: t.textPaddingX
        // option 文字距行顶内边距：覆盖后取代默认垂直居中（8px 字高假设对自定义字体不准）。
        val padY = d.optionPaddingY()
        // option 面板 / 行圆角：覆盖时走 styled 圆角矩形，否则保持直角 fillRect（零回归）。
        val cornerRadius = d.panelCornerRadius()
        var oy = box.y() + box.height()
        val oh = d.effectiveOptionHeight()
        val font = FontAccess.resolve(d.font())
        val fontName = font.effectiveName()
        val shadow = font.effectiveShadow()
        val bold = font.effectiveBold()
        val italic = font.effectiveItalic()
        val scale = d.size()
        // 面板/选项配色用控件覆盖属性（控件高度自定义）；字体走 HEAD 的 FontAccess。
        val panelColor = d.panelColor() ?: t.surface
        val selectedColor = d.selectedOptionColor() ?: t.primaryContainer
        val selectedTextColor = d.selectedOptionTextColor() ?: t.onPrimaryContainer
        val textColor = d.textColor() ?: t.onSurface
        for (opt in d.options()) {
            val selected = d.selectedValue() == opt.value()
            val rowBg = if (selected) selectedColor else panelColor
            val rowText = if (selected) selectedTextColor else textColor
            if (cornerRadius != null) {
                // 圆角行背景：走 ROUNDED_RECT styled batch。
                fillRounded(bridge, box.x(), oy, box.width(), oh, rowBg, cornerRadius)
            } else {
                bridge.fillRect(box.x(), oy, box.width(), oh, rowBg)
            }
            bridge.fillRect(box.x(), oy, box.width(), bw, border)
            bridge.fillRect(box.x(), oy + oh - bw, box.width(), bw, border)
            // 使用 label() 显示（label 非空时用 label，否则回退到 value()）；字体走 FontAccess。
            // 文字 Y：optionPaddingY 覆盖时距行顶固定内边距，否则按 8px 字高假设垂直居中。
            bridge.drawText(
                opt.label(),
                box.x() + pad,
                if (padY != null) oy + padY else oy + (oh - 8f) / 2f,
                rowText,
                scale,
                fontName,
                shadow,
                bold,
                italic,
            )
            oy += oh
        }
    }

    /** 圆角矩形填充：走 ROUNDED_RECT styled batch（仅当 panelCornerRadius 覆盖存在时使用）。 */
    private fun fillRounded(bridge: UiRenderBridge, x: Float, y: Float, w: Float, h: Float, color: Int, radius: Float) {
        val fill = StyledFill.builder()
            .cornerRadii(CornerRadii.uniform(radius).toArray())
            .borderWidth(0f)
            .borderColor(0)
            .build()
        val batch = RenderBatch(
            RenderCommandType.ROUNDED_RECT,
            null,
            RenderCommand.NO_CLIP,
            listOf(RenderCommand.roundedRect(x, y, w, h, color, RenderCommand.NO_CLIP, fill)),
        )
        bridge.submitBatch(batch)
    }
}
